import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const BEREICH_OPTIONS = ['Inspektion', 'Reifenwechsel', 'Versicherung', 'HU/SP', 'Allgemein', 'Sonstiges'];

const empty = {
  kennzeichen: '', fahrzeugtyp: '', marke: '', modell: '', farbe: '', fin: '',
  erstzulassung: '', hu_faelligkeit: '', sp_faelligkeit: '',
  naechste_inspektion_datum: '', naechste_inspektion_km: '',
  reifenart: '', reifengroesse: '', reifenzustand: '',
  kilometerstand: '', kraftstoffart: '',
  versicherungsgesellschaft: '', versicherungsnummer: '',
  versicherungsart: '', versicherungsablauf: '',
  finanzierungsart: '', monatliche_rate: '',
  vertragsbeginn: '', vertragsende: '', finanzierungsanbieter: '',
  leasing_km_gesamt: '', leasing_km_start: '', leasing_bereifung: '',
  hu_intervall_monate: 24, sp_intervall_monate: 6,
  inspektion_intervall_monate: 12, versicherung_intervall_monate: 12,
  notizen: '', kunden_id: '',
  ansprechpartner_assignments: []
};

// ─── AnsprechpartnerPicker ────────────────────────────────────────────────────

function AnsprechpartnerPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [kontakte, setKontakte] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // [{ansprechpartner_id, name, kontakt_name, bereich}]
  const timer = useRef(null);

  // Load initial assignments (when editing existing Fahrzeug)
  useEffect(() => {
    if (!value || value.length === 0) { setSelectedItems([]); return; }
    // value is array of {ansprechpartner_id, bereich}
    // We need to fetch names – but they should come from getFahrzeugAnsprechpartner
    // So we rely on them being passed as enriched objects
    if (value.length > 0 && value[0].name) {
      setSelectedItems(value.map(a => ({
        ansprechpartner_id: a.ansprechpartner_id,
        name: a.name,
        kontakt_name: a.kontakt_name || '',
        bereich: a.bereich || 'Allgemein'
      })));
    }
  }, []); // eslint-disable-line

  const search = (q) => {
    clearTimeout(timer.current);
    setQuery(q);
    if (!q.trim()) { setKontakte([]); setShowSuggestions(false); return; }
    timer.current = setTimeout(() => {
      api.searchKontaktbuch(q).then(results => {
        setKontakte(results);
        setShowSuggestions(true);
      }).catch(() => {});
    }, 300);
  };

  const addAnsprechpartner = (ap, kontakt) => {
    const already = selectedItems.some(s => s.ansprechpartner_id === ap.id);
    if (already) return;
    const newItem = {
      ansprechpartner_id: ap.id,
      name: ap.name,
      kontakt_name: kontakt.name,
      bereich: 'Allgemein'
    };
    const newItems = [...selectedItems, newItem];
    setSelectedItems(newItems);
    onChange(newItems.map(s => ({ ansprechpartner_id: s.ansprechpartner_id, bereich: s.bereich })));
    setQuery(''); setKontakte([]); setShowSuggestions(false);
  };

  const removeItem = (apId) => {
    const newItems = selectedItems.filter(s => s.ansprechpartner_id !== apId);
    setSelectedItems(newItems);
    onChange(newItems.map(s => ({ ansprechpartner_id: s.ansprechpartner_id, bereich: s.bereich })));
  };

  const updateBereich = (apId, bereich) => {
    const newItems = selectedItems.map(s => s.ansprechpartner_id === apId ? { ...s, bereich } : s);
    setSelectedItems(newItems);
    onChange(newItems.map(s => ({ ansprechpartner_id: s.ansprechpartner_id, bereich: s.bereich })));
  };

  return (
    <div>
      {selectedItems.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {selectedItems.map(item => (
            <div key={item.ansprechpartner_id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 8px 4px 12px', background: 'var(--primary-light)',
              border: '1px solid var(--primary)', borderRadius: 20,
              fontSize: 12, fontWeight: 500
            }}>
              <span>{item.name}</span>
              {item.kontakt_name && (
                <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 11 }}>· {item.kontakt_name}</span>
              )}
              <select
                value={item.bereich || 'Allgemein'}
                onChange={e => updateBereich(item.ansprechpartner_id, e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 11, border: '1px solid var(--primary)', borderRadius: 4, padding: '1px 4px', background: '#fff', cursor: 'pointer' }}
              >
                {BEREICH_OPTIONS.map(b => <option key={b}>{b}</option>)}
              </select>
              <button type="button" onClick={() => removeItem(item.ansprechpartner_id)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--gray-500)', fontWeight: 700, fontSize: 14, lineHeight: 1, padding: 0
              }}>×</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          className="form-input"
          placeholder="Kontakt / Firma suchen, dann Ansprechpartner auswählen…"
          value={query}
          onChange={e => search(e.target.value)}
          onFocus={() => query && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />
        {showSuggestions && kontakte.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
            background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,.1)', maxHeight: 300, overflowY: 'auto'
          }}>
            {kontakte.map(k => (
              <div key={k.id}>
                <div style={{
                  padding: '8px 14px', fontWeight: 700, fontSize: 12,
                  background: 'var(--gray-50)', color: 'var(--gray-600)',
                  borderBottom: '1px solid var(--gray-100)', textTransform: 'uppercase', letterSpacing: '0.05em'
                }}>
                  {k.name} {k.ort ? `· ${k.ort}` : ''}
                </div>
                {(k.ansprechpartner || []).length === 0 ? (
                  <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--gray-400)' }}>
                    Keine Ansprechpartner vorhanden
                  </div>
                ) : (
                  k.ansprechpartner.map(ap => {
                    const alreadySelected = selectedItems.some(s => s.ansprechpartner_id === ap.id);
                    return (
                      <div key={ap.id} onMouseDown={() => !alreadySelected && addAnsprechpartner(ap, k)} style={{
                        padding: '8px 14px 8px 24px', cursor: alreadySelected ? 'default' : 'pointer',
                        borderBottom: '1px solid var(--gray-100)',
                        opacity: alreadySelected ? 0.4 : 1, background: '#fff'
                      }}
                      onMouseEnter={e => { if (!alreadySelected) e.currentTarget.style.background = 'var(--gray-50)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{ap.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                          {ap.funktion && ap.funktion}{ap.telefon && ` · ${ap.telefon}`}
                          {alreadySelected && ' · bereits ausgewählt'}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ))}
          </div>
        )}
        {showSuggestions && kontakte.length === 0 && query.trim() && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
            background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
            padding: '10px 14px', fontSize: 13, color: 'var(--gray-500)'
          }}>
            Kein Kontakt gefunden. Zuerst im Kontaktbuch anlegen.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FahrzeugForm ─────────────────────────────────────────────────────────────

export default function FahrzeugForm({ initial, kundenId, alleMode, onSave, onClose }) {
  const [form, setForm] = useState(() => {
    if (initial) return {
      ...initial,
      hu_intervall_monate: initial.hu_intervall_monate ?? 24,
      sp_intervall_monate: initial.sp_intervall_monate ?? 6,
      inspektion_intervall_monate: initial.inspektion_intervall_monate ?? 12,
      versicherung_intervall_monate: initial.versicherung_intervall_monate ?? 12,
      leasing_bereifung: initial.leasing_bereifung || '',
      ansprechpartner_assignments: []
    };
    return { ...empty, kunden_id: kundenId || '' };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [kunden, setKunden] = useState([]);

  useEffect(() => {
    if (alleMode) api.getKunden().then(setKunden).catch(() => {});
  }, [alleMode]);

  // Load Ansprechpartner for existing vehicle
  useEffect(() => {
    if (initial?.id) {
      api.getFahrzeugAnsprechpartner(initial.id)
        .then(apList => {
          setForm(f => ({ ...f, ansprechpartner_assignments: apList }));
        })
        .catch(() => {});
    }
  }, [initial]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-set intervall when fahrzeugtyp changes to LKW
  const handleTypChange = (typ) => {
    set('fahrzeugtyp', typ);
    if (typ === 'LKW') {
      setForm(f => ({ ...f, fahrzeugtyp: typ, hu_intervall_monate: 12, sp_intervall_monate: 6 }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.kennzeichen.trim()) { setError('Kennzeichen ist erforderlich.'); return; }
    if (!form.fahrzeugtyp) { setError('Fahrzeugtyp ist erforderlich.'); return; }
    if (alleMode && !form.kunden_id) { setError('Bitte einen Kunden auswählen.'); return; }
    setSaving(true);
    try { await onSave(form); }
    catch (err) { setError(err.message || 'Fehler beim Speichern.'); setSaving(false); }
  };

  const isLeasing = form.finanzierungsart === 'Leasing';
  const hasVertrag = form.finanzierungsart === 'Leasing' || form.finanzierungsart === 'Finanzierung';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 940 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>

            {alleMode && (
              <>
                <div className="form-grid" style={{ padding: '16px 24px 0' }}>
                  <div className="form-group form-full">
                    <label className="form-label">Kunde *</label>
                    <select className="form-select" value={form.kunden_id} onChange={e => set('kunden_id', e.target.value)}>
                      <option value="">– Bitte wählen –</option>
                      {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}

            <div className="section-title">Fahrzeugdaten</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Fahrzeugtyp *</label>
                <select className="form-select" value={form.fahrzeugtyp || ''} onChange={e => handleTypChange(e.target.value)} autoFocus>
                  <option value="">– Bitte wählen –</option>
                  <option>PKW</option><option>LKW</option><option>Sonderfahrzeug</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Kennzeichen *</label>
                <input className="form-input" value={form.kennzeichen} onChange={e => set('kennzeichen', e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label className="form-label">Marke</label>
                <input className="form-input" value={form.marke || ''} onChange={e => set('marke', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Modell</label>
                <input className="form-input" value={form.modell || ''} onChange={e => set('modell', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Farbe</label>
                <input className="form-input" value={form.farbe || ''} onChange={e => set('farbe', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">FIN</label>
                <input className="form-input" value={form.fin || ''} onChange={e => set('fin', e.target.value.toUpperCase())} />
              </div>
              <div className="form-group">
                <label className="form-label">Erstzulassung</label>
                <input className="form-input" type="date" value={form.erstzulassung || ''} onChange={e => set('erstzulassung', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Kraftstoffart</label>
                <select className="form-select" value={form.kraftstoffart || ''} onChange={e => set('kraftstoffart', e.target.value)}>
                  <option value="">– Bitte wählen –</option>
                  <option>Benzin</option><option>Diesel</option><option>Elektro</option>
                  <option>Hybrid</option><option>Plug-in-Hybrid</option>
                  <option>LPG/Autogas</option><option>CNG/Erdgas</option><option>Wasserstoff</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Aktueller Kilometerstand</label>
                <input className="form-input" type="number" min="0" value={form.kilometerstand || ''} onChange={e => set('kilometerstand', e.target.value)} placeholder="wird in Historie gespeichert" />
              </div>
            </div>

            <div className="section-title">Prüftermine & Inspektion</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">HU-Fälligkeit</label>
                <input className="form-input" type="date" value={form.hu_faelligkeit || ''} onChange={e => set('hu_faelligkeit', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">HU-Intervall (Monate)</label>
                <input className="form-input" type="number" min="1" max="120" value={form.hu_intervall_monate ?? 24} onChange={e => set('hu_intervall_monate', parseInt(e.target.value) || 24)} />
              </div>
              <div className="form-group">
                <label className="form-label">SP-Fälligkeit</label>
                <input className="form-input" type="date" value={form.sp_faelligkeit || ''} onChange={e => set('sp_faelligkeit', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">SP-Intervall (Monate)</label>
                <input className="form-input" type="number" min="1" max="120" value={form.sp_intervall_monate ?? 6} onChange={e => set('sp_intervall_monate', parseInt(e.target.value) || 6)} />
              </div>
              <div className="form-group">
                <label className="form-label">Nächste Inspektion (Datum)</label>
                <input className="form-input" type="date" value={form.naechste_inspektion_datum || ''} onChange={e => set('naechste_inspektion_datum', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Inspektions-Intervall (Monate)</label>
                <input className="form-input" type="number" min="1" max="120" value={form.inspektion_intervall_monate ?? 12} onChange={e => set('inspektion_intervall_monate', parseInt(e.target.value) || 12)} />
              </div>
              <div className="form-group">
                <label className="form-label">Nächste Inspektion (km)</label>
                <input className="form-input" type="number" min="0" value={form.naechste_inspektion_km || ''} onChange={e => set('naechste_inspektion_km', e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: -8, marginBottom: 16, padding: '0 4px' }}>
              PKW Neuwagen: erste HU nach 36 Monaten, dann alle 24 Monate. LKW &gt;3,5t: HU jährlich (12), SP halbjährlich (6).
            </div>

            <div className="section-title">Versicherung</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Versicherungsgesellschaft</label>
                <input className="form-input" value={form.versicherungsgesellschaft || ''} onChange={e => set('versicherungsgesellschaft', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Versicherungsnummer</label>
                <input className="form-input" value={form.versicherungsnummer || ''} onChange={e => set('versicherungsnummer', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Versicherungsart</label>
                <select className="form-select" value={form.versicherungsart || ''} onChange={e => set('versicherungsart', e.target.value)}>
                  <option value="">– Bitte wählen –</option>
                  <option>Haftpflicht</option><option>Teilkasko</option><option>Vollkasko</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Versicherungsablauf</label>
                <input className="form-input" type="date" value={form.versicherungsablauf || ''} onChange={e => set('versicherungsablauf', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Versicherungs-Intervall (Monate)</label>
                <input className="form-input" type="number" min="1" max="120" value={form.versicherung_intervall_monate ?? 12} onChange={e => set('versicherung_intervall_monate', parseInt(e.target.value) || 12)} />
              </div>
            </div>

            <div className="section-title">Finanzierung / Leasing</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Finanzierungsart</label>
                <select className="form-select" value={form.finanzierungsart || ''} onChange={e => set('finanzierungsart', e.target.value)}>
                  <option value="">– Bitte wählen –</option>
                  <option>Kauf</option><option>Leasing</option><option>Finanzierung</option>
                </select>
              </div>
              {hasVertrag && <>
                <div className="form-group">
                  <label className="form-label">Anbieter / Bank</label>
                  <input className="form-input" value={form.finanzierungsanbieter || ''} onChange={e => set('finanzierungsanbieter', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Monatliche Rate (€)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={form.monatliche_rate || ''} onChange={e => set('monatliche_rate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Vertragsbeginn</label>
                  <input className="form-input" type="date" value={form.vertragsbeginn || ''} onChange={e => set('vertragsbeginn', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Vertragsende</label>
                  <input className="form-input" type="date" value={form.vertragsende || ''} onChange={e => set('vertragsende', e.target.value)} />
                </div>
              </>}
              {isLeasing && <>
                <div className="form-group">
                  <label className="form-label">Laufleistung laut Vertrag (km)</label>
                  <input className="form-input" type="number" min="0" value={form.leasing_km_gesamt || ''} onChange={e => set('leasing_km_gesamt', e.target.value)} placeholder="z.B. 45000" />
                </div>
                <div className="form-group">
                  <label className="form-label">km-Stand bei Vertragsbeginn</label>
                  <input className="form-input" type="number" min="0" value={form.leasing_km_start || ''} onChange={e => set('leasing_km_start', e.target.value)} placeholder="km-Stand bei Übergabe" />
                </div>
                <div className="form-group">
                  <label className="form-label">Bereifung</label>
                  <select className="form-select" value={form.leasing_bereifung || ''} onChange={e => set('leasing_bereifung', e.target.value)}>
                    <option value="">– Bitte wählen –</option>
                    <option value="4">4-fach bereift (ein Satz)</option>
                    <option value="8">8-fach bereift (zwei Sätze)</option>
                  </select>
                  {form.leasing_bereifung === '8' && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px' }}>
                      ⚠️ Bei 8-fach Bereifung: Der zweite Felgensatz muss am Vertragsende zurückgegeben werden.
                    </div>
                  )}
                </div>
              </>}
            </div>

            <div className="section-title">Zugeordnete Ansprechpartner</div>
            <div style={{ marginBottom: 16 }}>
              <AnsprechpartnerPicker
                value={form.ansprechpartner_assignments || []}
                onChange={assignments => set('ansprechpartner_assignments', assignments)}
              />
              <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6 }}>
                Ansprechpartner werden im Kontaktbuch verwaltet. Neue Kontakte zuerst dort anlegen.
              </div>
            </div>

            <div className="section-title">Notizen</div>
            <div className="form-grid">
              <div className="form-group form-full">
                <textarea className="form-textarea" rows={3} value={form.notizen || ''} onChange={e => set('notizen', e.target.value)} placeholder="Freie Notizen zum Fahrzeug…" />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
